import { Button } from "@light-technology/components";
import { skipToken } from "@reduxjs/toolkit/query";
import { Edit05, XClose } from "@untitledui/icons";
import type { UIMessage } from "ai";
import { lazy, Suspense, useEffect, useState } from "react";
import logo from "../../assets/logo.svg";
import { api } from "../../state/api";
import { chat } from "../../state/chat";
import { useTypedDispatch, useTypedSelector } from "../../state/store";
import { ChatLoader } from "./ChatLoader";
import { ChatResizeHandle } from "./ChatResizeHandle";

const ChatThread = lazy(() =>
  import("./ChatThread").then((m) => ({ default: m.ChatThread })),
);

// Shows nothing for the first second, then fades in a "thinking" bubble so
// that fast loads never flash the loader.
const DelayedChatFallback = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setShow(true);
    }, 1000);
    return () => {
      clearTimeout(t);
    };
  }, []);

  if (!show) return null;

  return (
    <div
      className="flex flex-auto flex-col justify-end px-xl pb-3xl"
      style={{ animation: "chat-loader-fadein 0.4s ease-out forwards" }}
    >
      <div className="pt-md">
        <ChatLoader />
      </div>
    </div>
  );
};

type ChatSlideoutProps = {
  onResizingChange: (isResizing: boolean) => void;
};

export const ChatSlideout = ({ onResizingChange }: ChatSlideoutProps) => {
  const dispatch = useTypedDispatch();
  const isOpen = useTypedSelector((s) => s.chat.isOpen);
  const initialQuestion = useTypedSelector((s) => s.chat.initialQuestion);

  const [threadUuid, setThreadUuid] = useState<string | null>(null);
  const [chatKey, setChatKey] = useState(0);
  const [hasOpened, setHasOpened] = useState(isOpen);
  if (isOpen && !hasOpened) setHasOpened(true);

  const threadQuery = api.useGetChatThreadQuery(
    hasOpened && threadUuid ? threadUuid : skipToken,
  );
  const messages = threadUuid
    ? threadQuery.data?.thread.messages.map((m): UIMessage => ({
        id: m.uuid,
        role: m.role === "user" ? "user" : "assistant",
        parts: [{ type: "text", text: m.content }],
      }))
    : undefined;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        dispatch(isOpen ? chat.actions.close() : chat.actions.open());
      } else if (e.key === "Escape" && isOpen) {
        const active = document.activeElement;
        const isInputFocused =
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement;
        if (!isInputFocused) dispatch(chat.actions.close());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch, isOpen]);

  const handleNewChat = () => {
    setThreadUuid(null);
    setChatKey((k) => k + 1);
  };

  // Start a new chat whenever initialQuestion is set to a non-null value.
  // We also sync prevInitialQuestion when it clears back to null so that
  // clicking the same suggestion after ChatThread clears it (null → same
  // question) is always detected as a change and triggers a new thread.
  const [prevInitialQuestion, setPrevInitialQuestion] =
    useState(initialQuestion);
  if (initialQuestion !== prevInitialQuestion) {
    setPrevInitialQuestion(initialQuestion);
    if (initialQuestion) handleNewChat();
  }

  const loader = <DelayedChatFallback />;

  return (
    <div
      className={`fixed inset-0 z-10 flex w-full flex-col bg-primary transition-transform duration-200 ease-in-out md:absolute ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* Header */}
      <div className="relative z-10 flex-none bg-primary">
        <div className="absolute top-3 right-2 flex items-center gap-1">
          {!!threadUuid && (
            <Button
              color="tertiary"
              type="button"
              onClick={handleNewChat}
              tooltip="Start new chat"
              iconLeading={<Edit05 size={16} />}
            />
          )}
          <Button
            color="tertiary"
            type="button"
            onClick={() => dispatch(chat.actions.close())}
            aria-label="Close"
            iconLeading={<XClose size={16} />}
          />
        </div>
        <div className="flex flex-col items-start gap-sm px-xl py-xl">
          <img src={logo} alt="Light" className="h-6" />
          <div className="flex items-center gap-md">
            <span className="text-2xl font-semibold text-primary">
              Ask Playbook
            </span>
            <kbd className="hidden items-center gap-0.5 rounded border border-secondary px-1 py-0.5 font-sans text-xs text-tertiary md:flex">
              <span>
                {/Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
                  ? "⌘"
                  : "Ctrl"}
              </span>
              <span>K</span>
            </kbd>
          </div>
        </div>
      </div>

      {hasOpened && (
        <Suspense fallback={loader}>
          <ChatThread
            key={chatKey}
            threadUuid={threadUuid}
            initialMessages={messages}
            initialQuestion={initialQuestion}
            isOpen={isOpen}
            onThreadCreated={setThreadUuid}
          />
        </Suspense>
      )}

      {isOpen && <ChatResizeHandle onResizingChange={onResizingChange} />}
    </div>
  );
};
